import React from "react";
import { ArrowRight, MapPin, Star, ShieldCheck } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { buildWhatsAppLink } from "../data/static";
import { formatPrice } from "../utils/currency";

export const Hero = () => {
  const { t } = useI18n();
  const h = t.hero;
  const ctaPrimary = buildWhatsAppLink(h.waMsg);

  return (
    <section id="home" data-testid="hero-section" className="relative pt-20 lg:pt-24 overflow-hidden bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-16 pb-16 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 space-y-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FADBD1] text-[#B34A2B] text-xs font-semibold tracking-wider uppercase">
              <MapPin className="w-3.5 h-3.5" />
              {h.badge}
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-[1.05] tracking-tight">
              {h.h1Part1}{" "}
              <span className="italic text-[#D1603D]">{h.h1Italic}</span>
              <br />
              {h.h1Part2}
            </h1>

            <p className="text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {h.subhead}
            </p>

            <div className="flex flex-wrap gap-3">
              <a href={ctaPrimary} target="_blank" rel="noopener noreferrer" data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D1603D] text-white text-sm font-semibold shadow-lg shadow-[#D1603D]/25 hover:bg-[#B34A2B] transition-all duration-300 active:scale-95 group">
                {h.ctaPrimary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#packages" data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 text-sm font-semibold hover:border-slate-900 transition-all duration-300 active:scale-95">
                {h.ctaSecondary}
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Star className="w-4 h-4 fill-[#FDE68A] text-[#D1603D]" />
                <span className="font-semibold">{h.ratingValue}</span> ·{" "}
                <span className="text-slate-500">{h.ratingTrips}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>{h.verified}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] w-full rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-900/15">
              <img src="https://images.pexels.com/photos/33290417/pexels-photo-33290417.jpeg?w=1200&auto=compress&cs=tinysrgb"
                alt="Hawa Mahal, Jaipur" className="w-full h-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/55 via-slate-900/10 to-transparent" />
              <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold tracking-wider uppercase text-slate-900">
                {h.heroChip}
              </div>
              <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[280px] bg-white rounded-2xl p-5 shadow-xl">
                <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#D1603D]">{h.todaysPick}</p>
                <p className="font-display text-lg font-semibold text-slate-900 mt-1 leading-snug">{h.pickTitle}</p>
                <p className="text-sm text-slate-500 mt-1">
                  {h.pickPriceLabel} <span className="font-semibold text-slate-900">{formatPrice(2700)}</span>{" "}
                  {h.pickPriceSuffix}
                </p>
                <a href="#packages" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D1603D] hover:gap-2 transition-all">
                  {h.seeItinerary} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            <div className="hidden lg:flex absolute -left-8 top-12 flex-col gap-3">
              <div className="w-16 h-16 rounded-full bg-[#FDE68A] animate-float-gentle" />
              <div className="w-10 h-10 rounded-full bg-[#D1603D]/90 ml-6" />
            </div>
          </div>
        </div>

        <div data-testid="trust-stats" className="mt-14 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {t.trustStats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 lg:p-6 border border-slate-200/80 hover:border-[#D1603D]/40 hover:-translate-y-1 transition-all duration-300">
              <div className="font-display text-3xl lg:text-4xl font-semibold text-slate-900">{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
